import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { frenchCatalog } from "../i18n";

const originals = new WeakMap<Node, string>();
const originalAttributes = new WeakMap<
  Element,
  Partial<Record<string, string>>
>();
const attributes = ["aria-label", "placeholder", "title"] as const;

function localize(root: ParentNode, french: boolean) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const current = node.textContent ?? "";
    const source = originals.get(node) ?? current;
    originals.set(node, source);
    const trimmed = source.trim();
    const translated = frenchCatalog[trimmed.replace(/\s+/g, " ")];
    if (french && translated)
      node.textContent = source.replace(trimmed, translated);
    else if (!french && current !== source) node.textContent = source;
  }
  root
    .querySelectorAll<HTMLElement>("[aria-label],[placeholder],[title]")
    .forEach((element) =>
      attributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;
        const saved = originalAttributes.get(element) ?? {};
        const source = saved[attribute] ?? value;
        saved[attribute] = source;
        originalAttributes.set(element, saved);
        element.setAttribute(
          attribute,
          french ? (frenchCatalog[source] ?? source) : source,
        );
      }),
    );
}

export function LocalizedApp({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const french = i18n.resolvedLanguage?.startsWith("fr") ?? false;
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    localize(root, french);
    const observer = new MutationObserver((records) =>
      records.forEach((record) =>
        record.addedNodes.forEach((node) => {
          if (node instanceof Element || node instanceof DocumentFragment)
            localize(node, french);
          else if (node.nodeType === Node.TEXT_NODE && node.parentNode)
            localize(node.parentNode, french);
        }),
      ),
    );
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [french]);
  return children;
}
